import type { UserUsage } from '../domain/types';
import { el } from './dom';

/**
 * A standing line of text saying what this account has spent and is allowed.
 *
 * Lives in the page footer, out of the way. The banners are for news - something is
 * nearly or fully gone - and interrupt accordingly; this is reference, and should be
 * findable without ever demanding attention. Between them they answer both questions:
 * "am I about to run out?" up top, "what am I allowed?" down here.
 *
 * Deliberately plain text rather than a meter. The numbers are small enough to read
 * directly, and a bar at 12/40 conveys less than "12 of 40" in the same space.
 */

/** Local time, because the reset is a moment in the user's day, not a UTC fact. */
function formatReset(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const withinADay = date.getTime() - Date.now() < 24 * 60 * 60 * 1000;
  return withinADay
    ? date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function usageSummary(usage: UserUsage | null): HTMLElement | null {
  if (!usage) return null;

  const daily = `${usage.daily.used} of ${usage.daily.limit} today`;
  const monthly = `${usage.monthly.used} of ${usage.monthly.limit} this month`;
  const reset = formatReset(usage.daily.resetsAt);

  return el(
    'footer',
    { class: 'usage', role: 'contentinfo' },
    el('span', { class: 'usage__label' }, 'Usage'),
    // A single text node rather than three spans: it has to wrap as one sentence on a
    // narrow screen, and separate elements would break at the separators instead.
    ` ${daily} · ${monthly}`,
    // Only the daily reset is named. The monthly one is weeks away and reads as noise
    // next to the number that actually runs out.
    reset ? el('span', { class: 'usage__reset' }, `Resets ${reset}`) : null,
  );
}
